'use client';

import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { DocumentNode } from 'graphql';

import { requestGraphQL, requestGraphQLWithResponse } from '../client';

export type UseGraphQLQueryOptions<TData, TVariables = Record<string, unknown>> = {
  query: DocumentNode | string;
  variables?: TVariables;
  queryKey: QueryKey;
  staleTime?: number;
  gcTime?: number;
  enabled?: boolean;
  /** When true, uses requestGraphQLWithResponse and exposes responseHeaders */
  withHeaders?: boolean;
};

export function useGraphQLQuery<TData, TVariables = Record<string, unknown>>({
  query,
  variables,
  queryKey,
  staleTime,
  gcTime,
  enabled = true,
  withHeaders = false,
}: UseGraphQLQueryOptions<TData, TVariables>) {
  const result = useQuery({
    queryKey,
    queryFn: async () => {
      if (withHeaders) {
        const { data, headers } = await requestGraphQLWithResponse<TData, TVariables>(
          query,
          variables
        );
        return { data, headers };
      }
      const data = await requestGraphQL<TData, TVariables>(query, variables);
      return { data };
    },
    staleTime,
    gcTime,
    enabled,
  });

  const responseData = result.data as { data: TData; headers?: Headers } | undefined;
  const data = responseData?.data;
  const responseHeaders = responseData?.headers;

  return {
    loading: result.isPending,
    error: result.error,
    data,
    refetch: result.refetch,
    ...(withHeaders && { responseHeaders }),
  };
}
